import React from 'react';
import {GraphNode, GraphEdge, QueryGraphLayoutAlgorithm} from '../interface';
import {calculateLayout} from './calculate-layout';

interface Props {
    setIsBrowserUnsupported: (val: boolean) => void;
    layoutAlgorithm: QueryGraphLayoutAlgorithm;
    nodes?: GraphNode[];
    edges?: GraphEdge[];
    onCalculationStarted?: () => void;
    onCalculated: (pos: string) => void;
    debug?: boolean;
    isHiddenNodesVisible?: boolean;
    isHiddenNode?: (nodeId: number) => boolean;
    isHiddenEdge?: (edgeId: number) => boolean;
}

/* This hook is mostly used to provide cancellation for previous layout calculation
 when the new calculation is starting */
export function useCalculateLayout({
    onCalculationStarted,
    onCalculated,
    setIsBrowserUnsupported,
    layoutAlgorithm,
    nodes,
    edges,
    isHiddenNodesVisible,
    isHiddenNode,
    isHiddenEdge
}: Props) {
    const [calculatedCounter, setCalculatedCounter] = React.useState(0);

    const terminateCalculationRef = React.useRef<() => void>(() => null);

    const calculateLayoutRef = React.useRef<() => ReturnType<typeof calculateLayout>>(undefined);

    calculateLayoutRef.current = () => {
        if (!nodes || !edges) {
            return {terminateCalculation: () => null};
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onCalculationStarted && onCalculationStarted();
        return calculateLayout({
            layoutAlgorithm,
            nodes: nodes.filter(
                (node) => !isHiddenNode || isHiddenNodesVisible || !isHiddenNode(node.id)
            ),
            edges: edges.filter(
                (edge) => !isHiddenEdge || isHiddenNodesVisible || !isHiddenEdge(edge.id)
            ),
            onCalculated
        });
    };

    React.useEffect(() => {
        if (!window.Worker) {
            setIsBrowserUnsupported(true);

            return;
        }

        if (calculatedCounter === 0) {
            return;
        }

        const {terminateCalculation} = calculateLayoutRef.current!();
        terminateCalculationRef.current = terminateCalculation;
        return terminateCalculation;
    }, [calculatedCounter, setIsBrowserUnsupported]);

    return {
        calculateLayout: React.useCallback(() => setCalculatedCounter((val) => val + 1), []),

        terminateLayoutCalculation: terminateCalculationRef.current
    };
}
